import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  Store, 
  ShoppingCart,
  CreditCard,
  Settings,
  DollarSign,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Crown,
  Ban,
  Key,
  Palette,
  BarChart3,
  Upload,
  Image
} from 'lucide-react';
import FinancialDashboard from './FinancialDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [systemSettings, setSystemSettings] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Package modal
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    max_outlets: 1,
    max_users: 5,
    features: [],
    is_active: true,
    sort_order: 0
  });
  const [featuresText, setFeaturesText] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Settings modal
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, usersRes, outletsRes, subscribersRes, packagesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/outlets`, { headers }),
        axios.get(`${API}/subscribers`, { headers }),
        axios.get(`${API}/subscription-packages?active_only=false`, { headers }),
        axios.get(`${API}/system-settings`, { headers })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setOutlets(outletsRes.data);
      setSubscribers(subscribersRes.data);
      setPackages(packagesRes.data);
      setSystemSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (e) {
      console.error('Failed to fetch data:', e);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/users/${userId}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success('User status updated');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${role}`, {}, { headers: getAuthHeader() });
      toast.success('User role updated');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleGrantLifetime = async (userId) => {
    try {
      await axios.post(`${API}/subscribers/${userId}/grant-lifetime`, {}, { headers: getAuthHeader() });
      toast.success('Lifetime access granted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userId) => {
    try {
      await axios.post(`${API}/subscribers/${userId}/revoke`, {}, { headers: getAuthHeader() });
      toast.success('Access revoked');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const handleSavePackage = async () => {
    if (!packageForm.name || !packageForm.display_name) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...packageForm,
        features: featuresText.split('\n').filter(f => f.trim())
      };
      
      if (editingPackage) {
        await axios.put(`${API}/subscription-packages/${editingPackage.id}`, data, { headers: getAuthHeader() });
        toast.success('Package updated');
      } else {
        await axios.post(`${API}/subscription-packages`, data, { headers: getAuthHeader() });
        toast.success('Package created');
      }
      
      setPackageModalOpen(false);
      setEditingPackage(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      await axios.delete(`${API}/subscription-packages/${packageId}`, { headers: getAuthHeader() });
      toast.success('Package deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete package');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/system-settings`, settingsForm, { headers: getAuthHeader() });
      toast.success('Settings saved');
      setSettingsModalOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/x-icon', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, SVG, ICO, or WebP file');
      return;
    }

    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-icon`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setSettingsForm(prev => ({ ...prev, app_icon_url: res.data.url }));
      toast.success('Icon uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  const openPackageModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setPackageForm({
        name: pkg.name,
        display_name: pkg.display_name,
        description: pkg.description || '',
        price_monthly: pkg.price_monthly,
        price_yearly: pkg.price_yearly || 0,
        max_outlets: pkg.max_outlets,
        max_users: pkg.max_users,
        features: pkg.features || [],
        is_active: pkg.is_active,
        sort_order: pkg.sort_order
      });
      setFeaturesText((pkg.features || []).join('\n'));
    } else {
      setEditingPackage(null);
      setPackageForm({
        name: '',
        display_name: '',
        description: '',
        price_monthly: 0,
        price_yearly: 0,
        max_outlets: 1,
        max_users: 5,
        features: [],
        is_active: true,
        sort_order: 0
      });
      setFeaturesText('');
    }
    setPackageModalOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="status-approved">Approved</Badge>;
      case 'pending':
        return <Badge className="status-pending">Pending</Badge>;
      case 'rejected':
        return <Badge className="status-rejected">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Crown className="h-8 w-8 text-amber-500" />
            Super Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Manage the entire platform from here.</p>
        </div>
        <Button onClick={() => setSettingsModalOpen(true)} data-testid="system-settings-btn">
          <Settings className="h-4 w-4 mr-2" />
          System Settings
        </Button>
      </div>

      {/* Stats */}
      <div className="bento-grid">
        <Card className="metric-card" data-testid="stat-users">
          <div className="flex items-center justify-between">
            <Users className="h-5 w-5 text-muted-foreground" />
            <span className="text-2xl font-bold">{stats?.total_users || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Total Users</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-outlets">
          <div className="flex items-center justify-between">
            <Store className="h-5 w-5 text-muted-foreground" />
            <span className="text-2xl font-bold">{stats?.total_outlets || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Total Outlets</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-orders">
          <div className="flex items-center justify-between">
            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
            <span className="text-2xl font-bold">{stats?.total_orders || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Total Orders</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-subscribers">
          <div className="flex items-center justify-between">
            <CreditCard className="h-5 w-5 text-emerald-500" />
            <span className="text-2xl font-bold">{stats?.total_subscribers || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Active Subscribers</p>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="analytics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="outlets">Outlets</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
        </TabsList>

        {/* Analytics Tab - Financial Dashboard */}
        <TabsContent value="analytics">
          <FinancialDashboard />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>All Users</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>{u.first_name} {u.last_name}</TableCell>
                      <TableCell className="font-mono text-sm">{u.email}</TableCell>
                      <TableCell>
                        <Select
                          value={u.role}
                          onValueChange={(value) => handleUpdateUserRole(u.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>{getStatusBadge(u.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {u.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateUserStatus(u.id, 'approved')}>
                              Approve
                            </Button>
                          )}
                          {u.status === 'approved' && u.role !== 'super_admin' && (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdateUserStatus(u.id, 'rejected')}>
                              Reject
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outlets Tab */}
        <TabsContent value="outlets">
          <Card>
            <CardHeader>
              <CardTitle>All Outlets</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company Reg</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outlets.map((outlet) => (
                    <TableRow key={outlet.id} data-testid={`outlet-row-${outlet.id}`}>
                      <TableCell className="font-medium">{outlet.name}</TableCell>
                      <TableCell className="font-mono text-sm">{outlet.company_reg_no}</TableCell>
                      <TableCell>{outlet.contact_no}</TableCell>
                      <TableCell>{new Date(outlet.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle>Subscribers</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.filter(u => u.role === 'admin').map((u) => {
                    const sub = subscribers.find(s => s.user_id === u.id);
                    const isLifetime = sub?.subscription_end && new Date(sub.subscription_end).getFullYear() >= 2050;
                    
                    return (
                      <TableRow key={u.id} data-testid={`sub-row-${u.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{u.first_name} {u.last_name}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {isLifetime ? (
                            <Badge className="bg-amber-500 text-white">Lifetime</Badge>
                          ) : sub?.subscription_tier ? (
                            <Badge variant="outline">{sub.subscription_tier}</Badge>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isLifetime ? 'Never' : sub?.subscription_end 
                            ? new Date(sub.subscription_end).toLocaleDateString() 
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {!isLifetime && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleGrantLifetime(u.id)}
                                data-testid={`grant-lifetime-${u.id}`}
                              >
                                <Crown className="h-4 w-4 mr-1" />
                                Grant Lifetime
                              </Button>
                            )}
                            {(sub?.subscribed || isLifetime) && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleRevokeAccess(u.id)}
                                data-testid={`revoke-${u.id}`}
                              >
                                <Ban className="h-4 w-4 mr-1" />
                                Revoke
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Subscription Packages</CardTitle>
              <Button onClick={() => openPackageModal()} data-testid="add-package-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Package
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {packages.map((pkg) => (
                  <Card key={pkg.id} className={`relative ${!pkg.is_active ? 'opacity-50' : ''}`} data-testid={`package-${pkg.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold">{pkg.display_name}</h3>
                          <p className="text-2xl font-bold">${pkg.price_monthly}<span className="text-sm font-normal">/mo</span></p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openPackageModal(pkg)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDeletePackage(pkg.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{pkg.description}</p>
                      <div className="text-sm">
                        <p>Outlets: {pkg.max_outlets}</p>
                        <p>Users: {pkg.max_users}</p>
                      </div>
                      {!pkg.is_active && (
                        <Badge variant="outline" className="mt-2">Inactive</Badge>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Package Modal */}
      <Dialog open={packageModalOpen} onOpenChange={setPackageModalOpen}>
        <DialogContent className="max-w-md" data-testid="package-modal">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Package' : 'Create Package'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name (slug)</Label>
                <Input
                  value={packageForm.name}
                  onChange={(e) => setPackageForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="basic"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={packageForm.display_name}
                  onChange={(e) => setPackageForm(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="Basic Plan"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={packageForm.description}
                onChange={(e) => setPackageForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Short description..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price Monthly ($)</Label>
                <Input
                  type="number"
                  value={packageForm.price_monthly}
                  onChange={(e) => setPackageForm(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Price Yearly ($)</Label>
                <Input
                  type="number"
                  value={packageForm.price_yearly}
                  onChange={(e) => setPackageForm(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Outlets</Label>
                <Input
                  type="number"
                  value={packageForm.max_outlets}
                  onChange={(e) => setPackageForm(prev => ({ ...prev, max_outlets: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Users</Label>
                <Input
                  type="number"
                  value={packageForm.max_users}
                  onChange={(e) => setPackageForm(prev => ({ ...prev, max_users: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Features (one per line)</Label>
              <Textarea
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackageModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePackage} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* System Settings Modal */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="max-w-lg" data-testid="system-settings-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              System Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Branding Section */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Branding
              </h3>
              
              {/* App Icon Upload */}
              <div className="space-y-2">
                <Label>App Icon</Label>
                <div className="flex items-center gap-4">
                  {settingsForm.app_icon_url ? (
                    <div className="h-16 w-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden">
                      <img 
                        src={settingsForm.app_icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settingsForm.app_icon_url}` : settingsForm.app_icon_url} 
                        alt="App Icon" 
                        className="h-full w-full object-contain"
                        data-testid="app-icon-preview"
                      />
                    </div>
                  ) : (
                    <div className="h-16 w-16 rounded-lg border border-dashed bg-muted flex items-center justify-center">
                      <Image className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" asChild disabled={uploadingIcon}>
                        <span>
                          {uploadingIcon ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                          {uploadingIcon ? 'Uploading...' : 'Upload Icon'}
                        </span>
                      </Button>
                      <input 
                        type="file" 
                        accept="image/png,image/jpeg,image/svg+xml,image/x-icon,image/webp"
                        onChange={handleIconUpload}
                        className="hidden"
                        data-testid="icon-upload-input"
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG, ICO, or WebP. Used as PWA icon.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={settingsForm.branding_company_name || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, branding_company_name: e.target.value }))}
                  placeholder="Deliman"
                />
              </div>
              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input
                  value={settingsForm.branding_logo_url || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, branding_logo_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settingsForm.branding_primary_color || '#2563EB'}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, branding_primary_color: e.target.value }))}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={settingsForm.branding_primary_color || '#2563EB'}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, branding_primary_color: e.target.value }))}
                    placeholder="#2563EB"
                  />
                </div>
              </div>
            </div>

            {/* Stripe Section */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Stripe Configuration
              </h3>
              <div className="space-y-2">
                <Label>Publishable Key</Label>
                <Input
                  value={settingsForm.stripe_publishable_key || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, stripe_publishable_key: e.target.value }))}
                  placeholder="pk_..."
                />
              </div>
              <div className="space-y-2">
                <Label>Secret Key</Label>
                <Input
                  type="password"
                  value={settingsForm.stripe_secret_key || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                  placeholder="sk_..."
                />
              </div>
            </div>

            {/* Email Section */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Key className="h-4 w-4" />
                Email Configuration
              </h3>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select
                  value={settingsForm.email_provider || ''}
                  onValueChange={(value) => setSettingsForm(prev => ({ ...prev, email_provider: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                    <SelectItem value="resend">Resend</SelectItem>
                    <SelectItem value="smtp">SMTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={settingsForm.email_api_key || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, email_api_key: e.target.value }))}
                  placeholder="Enter API key..."
                />
              </div>
              <div className="space-y-2">
                <Label>From Address</Label>
                <Input
                  value={settingsForm.email_from_address || ''}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, email_from_address: e.target.value }))}
                  placeholder="noreply@yourcompany.com"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
